import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
    Key, Webhook, Calendar, Shield, ArrowLeft, Plus, Trash2, Copy, Eye, EyeOff,
    Clock, AlertTriangle, CheckCircle, Settings, RefreshCw, Database
} from 'lucide-react';
import { enterpriseAPI, schedulesAPI, agentsAPI, accountAPI } from '@/lib/api';

const SettingsPage = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('api-keys');
    const [loading, setLoading] = useState(false);
    
    // API Keys state
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyPermissions, setNewKeyPermissions] = useState(['read']);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [showKey, setShowKey] = useState(false);
    
    // Schedules state
    const [schedules, setSchedules] = useState([]);
    const [agents, setAgents] = useState([]);
    const [newSchedule, setNewSchedule] = useState({
        agentId: '',
        name: '',
        frequency: 'daily',
        scanType: 'quick',
        hour: 2
    });
    
    // Webhooks state
    const [webhooks, setWebhooks] = useState([]);
    const [newWebhook, setNewWebhook] = useState({
        name: '',
        url: '',
        events: ['threat.detected']
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [keysRes, schedulesRes, agentsRes, webhooksRes] = await Promise.all([
                enterpriseAPI.listApiKeys().catch(() => ({ data: [] })),
                schedulesAPI.list().catch(() => ({ data: [] })),
                agentsAPI.list().catch(() => ({ data: [] })),
                enterpriseAPI.listWebhooks().catch(() => ({ data: [] }))
            ]);
            setApiKeys(keysRes.data || []);
            setSchedules(schedulesRes.data || []);
            setAgents(agentsRes.data || []);
            setWebhooks(webhooksRes.data || []);
        } catch (error) {
            console.error('Error loading settings data:', error);
        } finally {
            setLoading(false);
        }
    };

    // API Key Functions
    const handleCreateApiKey = async () => {
        if (!newKeyName.trim()) {
            toast.error('Please enter a key name');
            return;
        }
        
        try {
            setLoading(true);
            const response = await enterpriseAPI.createApiKey(newKeyName, newKeyPermissions);
            setNewlyCreatedKey(response.data);
            setNewKeyName('');
            toast.success('API Key created!', {
                description: 'Copy your key now - it won\'t be shown again.'
            });
            loadData();
        } catch (error) {
            toast.error('Failed to create API key');
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeKey = async (keyId) => {
        if (!confirm('Are you sure you want to revoke this API key?')) return;
        
        try {
            await enterpriseAPI.revokeApiKey(keyId);
            toast.success('API key revoked');
            loadData();
        } catch (error) {
            toast.error('Failed to revoke key');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    // Schedule Functions
    const handleCreateSchedule = async () => {
        if (!newSchedule.agentId || !newSchedule.name.trim()) {
            toast.error('Please select an agent and enter a schedule name');
            return;
        }
        
        try {
            setLoading(true);
            await schedulesAPI.create(
                newSchedule.agentId,
                newSchedule.name,
                newSchedule.frequency,
                newSchedule.scanType,
                newSchedule.hour
            );
            toast.success('Scan schedule created!');
            setNewSchedule({ agentId: '', name: '', frequency: 'daily', scanType: 'quick', hour: 2 });
            loadData();
        } catch (error) {
            toast.error('Failed to create schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSchedule = async (scheduleId) => {
        if (!confirm('Delete this scan schedule?')) return;
        
        try {
            await schedulesAPI.delete(scheduleId);
            toast.success('Schedule deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete schedule');
        }
    };

    // Webhook Functions
    const handleCreateWebhook = async () => {
        if (!newWebhook.name.trim() || !newWebhook.url.trim()) {
            toast.error('Please enter webhook name and URL');
            return;
        }
        
        try {
            setLoading(true);
            const response = await enterpriseAPI.createWebhook(
                newWebhook.name,
                newWebhook.url,
                newWebhook.events
            );
            toast.success('Webhook created!', {
                description: `Secret: ${response.data.secret?.substring(0, 16)}...`
            });
            setNewWebhook({ name: '', url: '', events: ['threat.detected'] });
            loadData();
        } catch (error) {
            toast.error('Failed to create webhook');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWebhook = async (webhookId) => {
        if (!confirm('Delete this webhook?')) return;
        
        try {
            await enterpriseAPI.deleteWebhook(webhookId);
            toast.success('Webhook deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete webhook');
        }
    };

    const togglePermission = (perm) => {
        if (newKeyPermissions.includes(perm)) {
            setNewKeyPermissions(newKeyPermissions.filter(p => p !== perm));
        } else {
            setNewKeyPermissions([...newKeyPermissions, perm]);
        }
    };

    const toggleWebhookEvent = (event) => {
        if (newWebhook.events.includes(event)) {
            setNewWebhook({ ...newWebhook, events: newWebhook.events.filter(e => e !== event) });
        } else {
            setNewWebhook({ ...newWebhook, events: [...newWebhook.events, event] });
        }
    };

    const handleClearDemoData = async () => {
        if (!confirm('This will remove all demo devices, threats, and sample data from your account. Only data from real agents will be kept. Continue?')) {
            return;
        }
        
        try {
            setLoading(true);
            const response = await accountAPI.clearDemoData();
            const deleted = response.data.deleted;
            toast.success('Demo data cleared!', {
                description: `Removed ${deleted.devices} devices, ${deleted.threats} threats, ${deleted.scans} scans`
            });
            // Refresh the page to show updated data
            window.location.reload();
        } catch (error) {
            toast.error('Failed to clear demo data');
        } finally {
            setLoading(false);
        }
    };

    const sections = [
        { id: 'api-keys', label: 'API Keys', icon: Key },
        { id: 'schedules', label: 'Scan Schedules', icon: Calendar },
        { id: 'webhooks', label: 'Webhooks', icon: Webhook },
        { id: 'data', label: 'Data Management', icon: Database },
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Header */}
            <header className="border-b border-white/10 bg-[#0d0d14]/80 backdrop-blur-xl sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/dashboard')}
                        className="text-white/60 hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                    </Button>
                    <div className="flex-1" />
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Settings className="w-5 h-5 text-cyan-400" />
                        Settings
                    </h1>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Sidebar Navigation */}
                    <div className="lg:col-span-1">
                        <Card className="bg-[#0d0d14] border-white/10">
                            <CardContent className="p-4 space-y-2">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        onClick={() => setActiveSection(section.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                            activeSection === section.id
                                                ? 'bg-cyan-500/20 text-cyan-400'
                                                : 'text-white/60 hover:text-white hover:bg-white/5'
                                        }`}
                                    >
                                        <section.icon className="w-5 h-5" />
                                        {section.label}
                                    </button>
                                ))}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Content Area */}
                    <div className="lg:col-span-3 space-y-6">
                        {/* API Keys Section */}
                        {activeSection === 'api-keys' && (
                            <>
                                <Card className="bg-[#0d0d14] border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-white flex items-center gap-2">
                                            <Key className="w-5 h-5 text-cyan-400" />
                                            Enterprise API Keys
                                        </CardTitle>
                                        <CardDescription className="text-white/60">
                                            Create API keys to integrate Shiro Security with your SIEM, SOC tools, or custom applications.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Create New Key */}
                                        <div className="p-4 bg-white/5 rounded-lg space-y-4">
                                            <h3 className="font-medium text-white">Create New API Key</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="text-white/60">Key Name</Label>
                                                    <Input
                                                        value={newKeyName}
                                                        onChange={(e) => setNewKeyName(e.target.value)}
                                                        placeholder="e.g., SIEM Integration"
                                                        className="bg-white/5 border-white/20 text-white mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-white/60">Permissions</Label>
                                                    <div className="flex gap-2 mt-2">
                                                        {['read', 'write', 'admin'].map((perm) => (
                                                            <button
                                                                key={perm}
                                                                onClick={() => togglePermission(perm)}
                                                                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                                                    newKeyPermissions.includes(perm)
                                                                        ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                                                                        : 'bg-white/5 text-white/40 border border-white/10'
                                                                }`}
                                                            >
                                                                {perm}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <Button 
                                                onClick={handleCreateApiKey} 
                                                disabled={loading}
                                                className="bg-gradient-to-r from-cyan-500 to-purple-600"
                                            >
                                                <Plus className="w-4 h-4 mr-2" /> Create API Key
                                            </Button>
                                        </div>

                                        {/* Newly Created Key Alert */}
                                        {newlyCreatedKey && (
                                            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                                <div className="flex items-start gap-3">
                                                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-green-400">API Key Created!</h4>
                                                        <p className="text-sm text-white/60 mb-2">Copy this key now - it will not be shown again.</p>
                                                        <div className="flex items-center gap-2 bg-black/30 p-2 rounded font-mono text-sm">
                                                            <span className="flex-1 text-cyan-400 break-all">
                                                                {showKey ? newlyCreatedKey.api_key : '•'.repeat(40)}
                                                            </span>
                                                            <button onClick={() => setShowKey(!showKey)} className="p-1 hover:bg-white/10 rounded">
                                                                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                            </button>
                                                            <button onClick={() => copyToClipboard(newlyCreatedKey.api_key)} className="p-1 hover:bg-white/10 rounded">
                                                                <Copy className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Existing Keys */}
                                        <div className="space-y-3">
                                            <h3 className="font-medium text-white/80">Active API Keys</h3>
                                            {apiKeys.length === 0 ? (
                                                <p className="text-white/40 text-sm">No API keys created yet.</p>
                                            ) : (
                                                apiKeys.map((key) => (
                                                    <div key={key.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-white">{key.name}</span>
                                                                <Badge className="bg-white/10 text-white/60 text-xs">{key.key_prefix}...</Badge>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                {key.permissions?.map((p) => (
                                                                    <Badge key={p} className="bg-cyan-500/20 text-cyan-400 text-xs">{p}</Badge>
                                                                ))}
                                                                <span className="text-xs text-white/40">
                                                                    {key.request_count || 0} requests
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRevokeKey(key.id)}
                                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* API Usage Example */}
                                        <div className="p-4 bg-white/5 rounded-lg">
                                            <h3 className="font-medium text-white/80 mb-2">Usage Example</h3>
                                            <pre className="bg-black/30 p-3 rounded text-sm text-cyan-400 overflow-x-auto">
{`curl -X GET "https://shirosec.com/api/enterprise/stats" \\
     -H "X-API-Key: shiro_your_api_key_here"`}
                                            </pre>
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        )}

                        {/* Scan Schedules Section */}
                        {activeSection === 'schedules' && (
                            <Card className="bg-[#0d0d14] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-cyan-400" />
                                        Scan Schedules
                                    </CardTitle>
                                    <CardDescription className="text-white/60">
                                        Set up automatic security scans for your protected devices.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Create New Schedule */}
                                    <div className="p-4 bg-white/5 rounded-lg space-y-4">
                                        <h3 className="font-medium text-white">Create New Schedule</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div>
                                                <Label className="text-white/60">Agent</Label>
                                                <select
                                                    value={newSchedule.agentId}
                                                    onChange={(e) => setNewSchedule({ ...newSchedule, agentId: e.target.value })}
                                                    className="w-full mt-1 bg-white/5 border border-white/20 text-white rounded-md px-3 py-2"
                                                >
                                                    <option value="" className="bg-[#0d0d14]">Select agent...</option>
                                                    {agents.map((agent) => (
                                                        <option key={agent.agent_id} value={agent.agent_id} className="bg-[#0d0d14]">
                                                            {agent.hostname} ({agent.os})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-white/60">Schedule Name</Label>
                                                <Input
                                                    value={newSchedule.name}
                                                    onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                                                    placeholder="e.g., Daily Security Scan"
                                                    className="bg-white/5 border-white/20 text-white mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-white/60">Frequency</Label>
                                                <select
                                                    value={newSchedule.frequency}
                                                    onChange={(e) => setNewSchedule({ ...newSchedule, frequency: e.target.value })}
                                                    className="w-full mt-1 bg-white/5 border border-white/20 text-white rounded-md px-3 py-2"
                                                >
                                                    <option value="hourly" className="bg-[#0d0d14]">Hourly</option>
                                                    <option value="daily" className="bg-[#0d0d14]">Daily</option>
                                                    <option value="weekly" className="bg-[#0d0d14]">Weekly</option>
                                                    <option value="monthly" className="bg-[#0d0d14]">Monthly</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-white/60">Scan Type</Label>
                                                <select
                                                    value={newSchedule.scanType}
                                                    onChange={(e) => setNewSchedule({ ...newSchedule, scanType: e.target.value })}
                                                    className="w-full mt-1 bg-white/5 border border-white/20 text-white rounded-md px-3 py-2"
                                                >
                                                    <option value="quick" className="bg-[#0d0d14]">Quick Scan</option>
                                                    <option value="full" className="bg-[#0d0d14]">Full Scan</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-white/60">Hour (0-23)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="23"
                                                    value={newSchedule.hour}
                                                    onChange={(e) => setNewSchedule({ ...newSchedule, hour: parseInt(e.target.value) || 0 })}
                                                    className="bg-white/5 border-white/20 text-white mt-1"
                                                />
                                            </div>
                                        </div>
                                        <Button 
                                            onClick={handleCreateSchedule} 
                                            disabled={loading}
                                            className="bg-gradient-to-r from-cyan-500 to-purple-600"
                                        >
                                            <Plus className="w-4 h-4 mr-2" /> Create Schedule
                                        </Button>
                                    </div>

                                    {/* Existing Schedules */}
                                    <div className="space-y-3">
                                        <h3 className="font-medium text-white/80">Active Schedules</h3>
                                        {schedules.length === 0 ? (
                                            <p className="text-white/40 text-sm">No scan schedules created yet.</p>
                                        ) : (
                                            schedules.map((schedule) => (
                                                <div key={schedule.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-white">{schedule.name}</span>
                                                            <Badge className={schedule.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                                                {schedule.is_active ? 'Active' : 'Paused'}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-1 text-sm text-white/60">
                                                            <span className="flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {schedule.frequency} at {schedule.hour}:00
                                                            </span>
                                                            <span>Agent: {schedule.agent_id?.substring(0, 8)}...</span>
                                                            {schedule.next_run_at && (
                                                                <span className="text-cyan-400">
                                                                    Next: {new Date(schedule.next_run_at).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteSchedule(schedule.id)}
                                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Webhooks Section */}
                        {activeSection === 'webhooks' && (
                            <Card className="bg-[#0d0d14] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <Webhook className="w-5 h-5 text-cyan-400" />
                                        Webhooks
                                    </CardTitle>
                                    <CardDescription className="text-white/60">
                                        Receive real-time notifications when security events occur.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Create New Webhook */}
                                    <div className="p-4 bg-white/5 rounded-lg space-y-4">
                                        <h3 className="font-medium text-white">Create New Webhook</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-white/60">Webhook Name</Label>
                                                <Input
                                                    value={newWebhook.name}
                                                    onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                                                    placeholder="e.g., Slack Alerts"
                                                    className="bg-white/5 border-white/20 text-white mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-white/60">Webhook URL</Label>
                                                <Input
                                                    value={newWebhook.url}
                                                    onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                                                    placeholder="https://your-endpoint.com/webhook"
                                                    className="bg-white/5 border-white/20 text-white mt-1"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-white/60">Events</Label>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {['threat.detected', 'threat.resolved', 'scan.completed', 'scan.failed', 'agent.online', 'agent.offline'].map((event) => (
                                                    <button
                                                        key={event}
                                                        onClick={() => toggleWebhookEvent(event)}
                                                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                                            newWebhook.events.includes(event)
                                                                ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                                                                : 'bg-white/5 text-white/40 border border-white/10'
                                                        }`}
                                                    >
                                                        {event}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <Button 
                                            onClick={handleCreateWebhook} 
                                            disabled={loading}
                                            className="bg-gradient-to-r from-cyan-500 to-purple-600"
                                        >
                                            <Plus className="w-4 h-4 mr-2" /> Create Webhook
                                        </Button>
                                    </div>

                                    {/* Existing Webhooks */}
                                    <div className="space-y-3">
                                        <h3 className="font-medium text-white/80">Active Webhooks</h3>
                                        {webhooks.length === 0 ? (
                                            <p className="text-white/40 text-sm">No webhooks configured yet.</p>
                                        ) : (
                                            webhooks.map((webhook) => (
                                                <div key={webhook.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-white">{webhook.name}</span>
                                                            <Badge className={webhook.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                                                                {webhook.is_active ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-white/40 mt-1 truncate max-w-md">{webhook.url}</p>
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {webhook.events?.map((event) => (
                                                                <Badge key={event} className="bg-purple-500/20 text-purple-400 text-xs">
                                                                    {event}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteWebhook(webhook.id)}
                                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Data Management Section */}
                        {activeSection === 'data' && (
                            <Card className="bg-[#0d0d14] border-white/10">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Database className="w-5 h-5 text-cyan-400" />
                                        Data Management
                                    </CardTitle>
                                    <CardDescription>Manage your account data and clear demo content</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                        <h3 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4" />
                                            Clear Demo Data
                                        </h3>
                                        <p className="text-sm text-white/60 mb-4">
                                            Remove all demo devices, sample threats, and test data from your account.
                                            This will keep only real data from your registered agents.
                                        </p>
                                        <Button
                                            onClick={handleClearDemoData}
                                            disabled={loading}
                                            variant="destructive"
                                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                                            data-testid="clear-demo-data-btn"
                                        >
                                            {loading ? (
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4 mr-2" />
                                            )}
                                            Clear Demo Data
                                        </Button>
                                    </div>
                                    
                                    <div className="text-xs text-white/40 p-4 bg-white/5 rounded-lg">
                                        <p><strong>What gets removed:</strong></p>
                                        <ul className="list-disc list-inside mt-2 space-y-1">
                                            <li>Demo devices (Main Workstation, MacBook Pro, etc.)</li>
                                            <li>Sample threats (Phishing Email, Brute Force, etc.)</li>
                                            <li>Orphaned scan reports</li>
                                        </ul>
                                        <p className="mt-3"><strong>What stays:</strong></p>
                                        <ul className="list-disc list-inside mt-2 space-y-1">
                                            <li>Real agents you&apos;ve installed</li>
                                            <li>Threats detected from real scans</li>
                                            <li>Your API keys and webhooks</li>
                                        </ul>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SettingsPage;
